import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ClientNotificationEventType,
  ClientNotificationScopeRequest,
  ClientNotificationSubscriptionRequest,
  ClientNotificationSubscriptionResponse,
} from '@payer-admin-ui/api/src/notifications/models';
import { notificationSubscriptionAPI } from './notificationSubscriptionAPI';

export type NotificationSubscriptionsState = {
  data: ClientNotificationSubscriptionResponse[];
  isFetching: boolean;
  error: unknown | null;
  isSaving: boolean;
  saveError: unknown | null;
  draftsByEventType: Partial<Record<ClientNotificationEventType, ClientNotificationSubscriptionRequest>>;
};

const initialState: NotificationSubscriptionsState = {
  data: [],
  isFetching: false,
  error: null,
  isSaving: false,
  saveError: null,
  draftsByEventType: {},
};

export const loadNotificationSubscriptions = createAsyncThunk<ClientNotificationSubscriptionResponse[]>(
  'notificationSubscriptions/load',
  async () => {
    return notificationSubscriptionAPI.get();
  },
);

export const saveNotificationSubscriptions = createAsyncThunk<
  void,
  ClientNotificationSubscriptionRequest,
  { state: { notificationSubscriptions: NotificationSubscriptionsState } }
>('notificationSubscriptions/save', async (request, { getState }) => {
  await notificationSubscriptionAPI.upsert(request);
});

const notificationSubscriptionsSlice = createSlice({
  name: 'notificationSubscriptions',
  initialState,
  reducers: {
    ensureDraftForEventType: (state, { payload }: PayloadAction<ClientNotificationEventType>) => {
      if (state.draftsByEventType[payload]) return;
      const existing = state.data.find(s => s.eventType === payload);
      state.draftsByEventType[payload] = {
        eventType: payload,
        scopes: existing ? existing.scopes.map(s => ({ ...s })) : [],
      };
    },
    setDraftScopeForEventType: (
      state,
      {
        payload,
      }: PayloadAction<{
        eventType: ClientNotificationEventType;
        scopes: ClientNotificationScopeRequest[];
      }>,
    ) => {
      const { eventType, scopes } = payload;
      if (!state.draftsByEventType[eventType]) {
        state.draftsByEventType[eventType] = { eventType, scopes: [] };
      }
      state.draftsByEventType[eventType].scopes = scopes.map(s => ({ ...s }));
    },
    setLocalSubscriptions: (state, { payload }: PayloadAction<ClientNotificationSubscriptionResponse[]>) => {
      state.data = payload;
    },
    resetErrors: state => {
      state.error = null;
      state.saveError = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(loadNotificationSubscriptions.pending, state => {
      state.isFetching = true;
      state.error = null;
    });
    builder.addCase(loadNotificationSubscriptions.fulfilled, (state, { payload }) => {
      state.isFetching = false;
      state.data = payload;
      payload.forEach(s => {
        state.draftsByEventType[s.eventType] = {
          eventType: s.eventType,
          scopes: s.scopes.map(scope => ({ ...scope })),
        };
      });
    });
    builder.addCase(loadNotificationSubscriptions.rejected, (state, { error }) => {
      state.isFetching = false;
      state.error = error;
    });

    builder.addCase(saveNotificationSubscriptions.pending, state => {
      state.isSaving = true;
      state.saveError = null;
    });
    builder.addCase(saveNotificationSubscriptions.fulfilled, state => {
      state.isSaving = false;
    });
    builder.addCase(saveNotificationSubscriptions.rejected, (state, { error }) => {
      state.isSaving = false;
      state.saveError = error;
    });
  },
});

export const {
  actions: { setLocalSubscriptions, resetErrors },
  reducer: notificationSubscriptionsReducer,
} = notificationSubscriptionsSlice;

export const ensureDraftForEventType = notificationSubscriptionsSlice.actions.ensureDraftForEventType;
export const setDraftScopeForEventType = notificationSubscriptionsSlice.actions.setDraftScopeForEventType;
